import {
  FolderCopyRounded,
  LocalHospitalRounded,
  PersonRounded,
  SupportAgentRounded,
} from "@mui/icons-material";
import { alpha } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import { brand, green, orange, red } from "../shared-theme/themePrimitives";
import axiosInstance from "../utils/axiosInstance";
import { checkPermission } from "../utils/checkPermission";
import ContentViewsBarChart from "./ContentViewsBarChart";
import MostDownloadedPDFs from "./MostDownloadedPDFs";
import RecentContents from "./RecentContents";
import RecentSupportRequests from "./RecentSupportRequests";
import StatCard from "./StatCard";
import UserActivityChart from "./UserActivityChart";

export default function MainGrid() {
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/stats`,
      );

      if (response.status === 200) {
        setStats(response.data);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
      {/* cards */}
      <Typography component="h2" variant="h3" sx={{ mb: 2 }}>
        Dashboard
      </Typography>

      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        <Grid key={0} size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Users"
            value={
              stats
                ? Object.values(stats.total_counts.users).reduce(
                    (total, value) => total + value,
                    0,
                  )
                : "N/A"
            }
            description="Total number of application users"
            icon={<PersonRounded fontSize="large" />}
            color={alpha(red[600], 0.3)}
            route="/users"
          />
        </Grid>
        <Grid key={0} size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Experts"
            value={
              stats
                ? Object.values(stats.total_counts.experts).reduce(
                    (total, value) => total + value,
                    0,
                  )
                : "N/A"
            }
            description="Total number of experts"
            icon={<SupportAgentRounded fontSize="large" />}
            color={alpha(brand[600], 0.3)}
            route="/users"
          />
        </Grid>
        <Grid key={0} size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Service Centers"
            value={
              stats
                ? Object.values(stats.total_counts.service_centers).reduce(
                    (total, value) => total + value,
                    0,
                  )
                : "N/A"
            }
            description="Total number of service centers"
            icon={<LocalHospitalRounded fontSize="large" />}
            color={alpha(green[600], 0.3)}
            route="/service-centers"
          />
        </Grid>
        <Grid key={0} size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Contents"
            value={
              stats
                ? Object.values(stats.total_counts.content).reduce(
                    (total, value) => total + value,
                    0,
                  )
                : "N/A"
            }
            description="Total number of contents"
            icon={<FolderCopyRounded fontSize="large" />}
            color={alpha(orange[600], 0.3)}
            route="/contents"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          {checkPermission("user-list") && (
            <UserActivityChart />
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          {checkPermission("content-list") &&
            checkPermission("content-view") && <ContentViewsBarChart />}
        </Grid>
      </Grid>

      <Grid container spacing={2} columns={12}>
        <Grid size={{ xs: 12, lg: 9 }}>
          {checkPermission("content-list") &&
            checkPermission("content-view") && <RecentContents />}
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Stack gap={2} direction={{ xs: "column", sm: "row", lg: "column" }}>
            {checkPermission("support-list") &&
              checkPermission("support-view") && <RecentSupportRequests />}
            {checkPermission("content-list") &&
              checkPermission("content-view") && <MostDownloadedPDFs />}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
